import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Slider,
  Typography,
  Button,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  type SelectChangeEvent,
  CircularProgress,
  Collapse,
  IconButton,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useGetSimpleWorkoutsQuery } from '../../../store/api/baseApi';

/**
 * Sport types available for filtering activities
 */
const SPORT_TYPES = [
  { value: 'all', label: 'All Sports' },
  { value: 'Run', label: 'Run' },
  { value: 'Ride', label: 'Ride' },
  { value: 'Swim', label: 'Swim' },
  { value: 'Walk', label: 'Walk' },
  { value: 'Hike', label: 'Hike' },
  { value: 'VirtualRide', label: 'Virtual Ride' },
  { value: 'VirtualRun', label: 'Virtual Run' },
  { value: 'Workout', label: 'Workout' },
  { value: 'WeightTraining', label: 'Weight Training' },
  { value: 'Yoga', label: 'Yoga' },
];

/**
 * Interface for activity filter values
 */
export interface ActivityFilterValues {
  startDate: Date | null;
  endDate: Date | null;
  sportType: string;
  distanceRange: [number, number];
  searchQuery: string;
  workoutId: number | 'all';
}

/**
 * Props for ActivityFilters component
 */
interface ActivityFiltersProps {
  filters: ActivityFilterValues;
  onFiltersChange: (filters: ActivityFilterValues) => void;
  onReset?: () => void;
}

/**
 * ActivityFilters Component
 * 
 * Provides comprehensive filtering capabilities for activities list including:
 * - Date range selection (start and end dates)
 * - Sport type dropdown filter
 * - Distance range slider (0-200 km)
 * - Search input for activity names
 * - Reset filters functionality
 * 
 * Supports theme-aware styling using CSS variables for consistent appearance
 * in both light and dark modes.
 * 
 * @param {ActivityFiltersProps} props - Component props
 * @returns {JSX.Element} Rendered filter component
 */
const ActivityFilters: React.FC<ActivityFiltersProps> = ({
  filters,
  onFiltersChange,
  onReset,
}) => {
  // State for expanded/collapsed filters
  const [expanded, setExpanded] = useState(false);

  // Fetch simple workouts for filtering
  const { data: simpleWorkoutsData, isLoading: isLoadingWorkouts } = useGetSimpleWorkoutsQuery({});

  const handleToggleExpanded = () => {
    setExpanded(!expanded);
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.workoutId !== 'all') count++;
    if (filters.sportType !== 'all') count++;
    if (filters.startDate) count++;
    if (filters.endDate) count++;
    if (filters.searchQuery) count++;
    if (filters.distanceRange[0] !== 0 || filters.distanceRange[1] !== 200) count++;
    return count;
  }, [filters]);

  /**
   * Handle start date change
   */
  const handleStartDateChange = (date: Date | null) => {
    onFiltersChange({
      ...filters,
      startDate: date,
    });
  };

  /**
   * Handle end date change
   */
  const handleEndDateChange = (date: Date | null) => {
    onFiltersChange({
      ...filters,
      endDate: date,
    });
  };

  /**
   * Handle sport type selection change
   */
  const handleSportTypeChange = (event: SelectChangeEvent<string>) => {
    onFiltersChange({
      ...filters,
      sportType: event.target.value,
    });
  };

  /**
   * Handle workout selection change
   */
  const handleWorkoutChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value === 'all' ? 'all' : Number(event.target.value);
    onFiltersChange({
      ...filters,
      workoutId: value,
    });
  };

  /**
   * Handle distance range slider change
   */
  const handleDistanceRangeChange = (
    _event: Event,
    newValue: number | number[]
  ) => {
    onFiltersChange({
      ...filters,
      distanceRange: newValue as [number, number],
    });
  };

  /**
   * Handle search query input change
   */
  const handleSearchQueryChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onFiltersChange({
      ...filters,
      searchQuery: event.target.value,
    });
  };

  /**
   * Handle reset filters button click
   */
  const handleReset = () => {
    if (onReset) {
      onReset();
    }
  };

  /**
   * Format distance value for slider display
   */
  const formatDistanceValue = (value: number): string => {
    return `${value} km`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
          }}
          onClick={handleToggleExpanded}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" component="h2" color="text.primary">
              Filter Activities
            </Typography>
            {activeFilterCount > 0 && (
              <Chip
                label={activeFilterCount}
                size="small"
                color="primary"
                sx={{ height: 20, minWidth: 20 }}
              />
            )}
          </Box>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ p: 3, pt: 0 }}>
            <Grid container spacing={3}>
          {/* Search Input */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search Activities"
              placeholder="Search by activity name..."
              value={filters.searchQuery}
              onChange={handleSearchQueryChange}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.default',
                  '& fieldset': {
                    borderColor: 'divider',
                  },
                  '&:hover fieldset': {
                    borderColor: 'text.secondary',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                },
                '& .MuiInputBase-input': {
                  color: 'text.primary',
                },
              }}
            />
          </Grid>

          {/* Sport Type Selector */}
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.default',
                  '& fieldset': {
                    borderColor: 'divider',
                  },
                  '&:hover fieldset': {
                    borderColor: 'text.secondary',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                },
                '& .MuiSelect-select': {
                  color: 'text.primary',
                },
              }}
            >
              <InputLabel id="sport-type-label">Sport Type</InputLabel>
              <Select
                labelId="sport-type-label"
                id="sport-type-select"
                value={filters.sportType}
                onChange={handleSportTypeChange}
                label="Sport Type"
              >
                {SPORT_TYPES.map((sport) => (
                  <MenuItem
                    key={sport.value}
                    value={sport.value}
                    sx={{
                      color: 'text.primary',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                        '&:hover': {
                          backgroundColor: 'action.selected',
                        },
                      },
                    }}
                  >
                    {sport.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Workout Selector */}
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.default',
                  '& fieldset': {
                    borderColor: 'divider',
                  },
                  '&:hover fieldset': {
                    borderColor: 'text.secondary',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                },
                '& .MuiSelect-select': {
                  color: 'text.primary',
                },
              }}
            >
              <InputLabel id="workout-label">Workout</InputLabel>
              <Select
                labelId="workout-label"
                id="workout-select"
                value={String(filters.workoutId)}
                onChange={handleWorkoutChange}
                label="Workout"
                disabled={isLoadingWorkouts}
              >
                <MenuItem
                  value="all"
                  sx={{
                    color: 'text.primary',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'action.selected',
                      '&:hover': {
                        backgroundColor: 'action.selected',
                      },
                    },
                  }}
                >
                  All Workouts
                </MenuItem>
                {isLoadingWorkouts ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading workouts...
                  </MenuItem>
                ) : simpleWorkoutsData && simpleWorkoutsData.length > 0 ? (
                  simpleWorkoutsData.map((workout) => (
                    <MenuItem
                      key={workout.id}
                      value={String(workout.id)}
                      sx={{
                        color: 'text.primary',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'action.selected',
                          '&:hover': {
                            backgroundColor: 'action.selected',
                          },
                        },
                      }}
                    >
                      {workout.name}
                    </MenuItem>
                  ))
                ) : null}
              </Select>
            </FormControl>
          </Grid>

          {/* Start Date Picker */}
          <Grid item xs={12} md={6}>
            <DatePicker
              label="Start Date"
              value={filters.startDate}
              onChange={handleStartDateChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.default',
                      '& fieldset': {
                        borderColor: 'divider',
                      },
                      '&:hover fieldset': {
                        borderColor: 'text.secondary',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'text.secondary',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    },
                  },
                },
              }}
            />
          </Grid>

          {/* End Date Picker */}
          <Grid item xs={12} md={6}>
            <DatePicker
              label="End Date"
              value={filters.endDate}
              onChange={handleEndDateChange}
              minDate={filters.startDate || undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.default',
                      '& fieldset': {
                        borderColor: 'divider',
                      },
                      '&:hover fieldset': {
                        borderColor: 'text.secondary',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'text.secondary',
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    },
                  },
                },
              }}
            />
          </Grid>

          {/* Distance Range Slider */}
          <Grid item xs={12}>
            <Box sx={{ px: 2 }}>
              <Typography
                id="distance-range-slider"
                gutterBottom
                variant="body2"
                color="text.secondary"
              >
                Distance Range (km)
              </Typography>
              <Slider
                value={filters.distanceRange}
                onChange={handleDistanceRangeChange}
                valueLabelDisplay="auto"
                valueLabelFormat={formatDistanceValue}
                min={0}
                max={200}
                step={5}
                marks={[
                  { value: 0, label: '0 km' },
                  { value: 50, label: '50 km' },
                  { value: 100, label: '100 km' },
                  { value: 150, label: '150 km' },
                  { value: 200, label: '200 km' },
                ]}
                aria-labelledby="distance-range-slider"
                sx={{
                  color: 'primary.main',
                  '& .MuiSlider-thumb': {
                    backgroundColor: 'primary.main',
                    border: '2px solid',
                    borderColor: 'background.paper',
                    '&:hover, &.Mui-focusVisible': {
                      boxShadow: '0px 0px 0px 8px rgba(13, 71, 161, 0.16)',
                    },
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: 'primary.main',
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: 'divider',
                  },
                  '& .MuiSlider-mark': {
                    backgroundColor: 'divider',
                  },
                  '& .MuiSlider-markLabel': {
                    color: 'text.secondary',
                  },
                  '& .MuiSlider-valueLabel': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                  },
                }}
              />
            </Box>
          </Grid>

          {/* Reset Button */}
          {onReset && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleReset}
                  color="secondary"
                  sx={{
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: 'text.secondary',
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  Reset Filters
                </Button>
              </Box>
            </Grid>
          )}
            </Grid>
          </Box>
        </Collapse>
      </Paper>
    </LocalizationProvider>
  );
};

export default ActivityFilters;